/**
 * OMNI iQ Unified Design System
 *
 * Combines Pod Ops design patterns with OmniIQ brand identity
 *
 * Design Principles:
 * 1. Pod Ops patterns for consistency: typography, spacing, layout
 * 2. OmniIQ brand colors for identity
 * 3. Domain-specific colors for air quality and device status
 * 4. Minimal color usage - neutrals dominate, color = meaning
 * 5. Maximum clarity without clutter
 */

export const theme = {
  // ==========================================
  // COLORS
  // ==========================================
  colors: {
    // Neutrals (Pod Ops foundation)
    background: '#FFFFFF',
    backgroundLight: '#FAFAFA',      // Light neutral surface (alias of surface) for subtle section/card backgrounds
    surface: '#FAFAFA',              // Barely perceptible off-white
    surfaceHover: '#F5F5F5',

    border: '#E8E8E8',
    borderHover: '#D1D1D1',

    text: {
      primary: '#0A0A0A',            // Near-black for maximum contrast
      secondary: '#666666',           // Medium gray for secondary info
      tertiary: '#999999',            // Light gray for tertiary info
      disabled: '#CCCCCC',            // Disabled state
    },

    // Brand Primary (OmniIQ Blue)
    primary: '#0066CC',               // OCA Blue - primary actions only
    primaryHover: '#0052A3',          // Darker blue on hover
    primaryLight: '#E6F2FF',          // Light blue for backgrounds/selected states
    primaryDark: '#004080',           // Dark blue for text on light backgrounds

    // Semantic Colors (Pod Ops style - used ONLY for status/alerts/actions)
    success: '#10B981',               // Emerald green
    successLight: '#D1FAE5',
    successDark: '#059669',

    warning: '#F59E0B',               // Amber
    warningLight: '#FEF3C7',
    warningDark: '#D97706',

    error: '#EF4444',                 // Red
    errorLight: '#FEE2E2',
    errorDark: '#DC2626',

    info: '#3B82F6',                  // Blue
    infoLight: '#DBEAFE',
    infoDark: '#2563EB',

    // Air Quality Status (OmniIQ Domain-Specific)
    // Industry-standard EPA Air Quality Index colors
    aqStatus: {
      good: '#00A651',                // Green (AQI 0-50)
      moderate: '#FFB81C',            // Yellow (AQI 51-100)
      unhealthySensitive: '#FF7E00',  // Orange (AQI 101-150)
      unhealthy: '#EE2737',           // Red (AQI 151-200)
      veryUnhealthy: '#8B008B',       // Purple (AQI 201-300)
      hazardous: '#7E0023',           // Maroon (AQI 301+)
      unknown: '#6C757D',             // Gray (no data)
    },

    // Device Status (OmniIQ Domain-Specific)
    deviceStatus: {
      online: '#10B981',              // Green (normal operation)
      offline: '#6C757D',             // Gray (no connection)
      warning: '#F59E0B',             // Amber (needs attention)
      error: '#EF4444',               // Red (failure)
      maintenance: '#3B82F6',         // Blue (scheduled maintenance)
      calibrating: '#8B5CF6',         // Purple (in progress)
    },

    // Chart Colors (data visualization)
    charts: {
      primary: '#0066CC',             // OCA Blue
      secondary: '#10B981',           // Emerald
      tertiary: '#F59E0B',            // Amber
      quaternary: '#8B5CF6',          // Purple
      quinary: '#EC4899',             // Pink

      // Time series / multi-line charts (ordered by visual distinction)
      series: [
        '#0066CC',  // OCA Blue
        '#10B981',  // Emerald
        '#F59E0B',  // Amber
        '#EF4444',  // Red
        '#8B5CF6',  // Purple
        '#EC4899',  // Pink
        '#14B8A6',  // Teal
        '#F97316',  // Orange
      ],

      // Heatmap gradients
      heatmap: {
        low: '#D1FAE5',               // Light green
        medium: '#10B981',            // Medium green
        high: '#059669',              // Dark green
      },
    },
  },

  // ==========================================
  // TYPOGRAPHY (Pod Ops)
  // ==========================================
  typography: {
    fontFamily: {
      primary: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      mono: '"JetBrains Mono", "Fira Code", "SF Mono", "Courier New", monospace',
    },

    // Display (32px / 600 / 40px line-height)
    display: {
      fontSize: '32px',
      fontWeight: '600',
      lineHeight: '40px',
      letterSpacing: '-0.02em',
    },

    // H1 (24px / 600 / 32px line-height)
    h1: {
      fontSize: '24px',
      fontWeight: '600',
      lineHeight: '32px',
      letterSpacing: '-0.01em',
    },

    // H2 (20px / 600 / 28px line-height)
    h2: {
      fontSize: '20px',
      fontWeight: '600',
      lineHeight: '28px',
      letterSpacing: '-0.01em',
    },

    // H3 (16px / 600 / 24px line-height)
    h3: {
      fontSize: '16px',
      fontWeight: '600',
      lineHeight: '24px',
      letterSpacing: '0',
    },

    // H4 (14px / 600 / 20px line-height) - minor heading
    h4: {
      fontSize: '14px',
      fontWeight: '600',
      lineHeight: '20px',
      letterSpacing: '0',
    },

    // H5 (12px / 600 / 16px line-height) - smallest heading / label
    h5: {
      fontSize: '12px',
      fontWeight: '600',
      lineHeight: '16px',
      letterSpacing: '0',
    },

    // Body (14px / 400 / 20px line-height) - default text
    body: {
      fontSize: '14px',
      fontWeight: '400',
      lineHeight: '20px',
      letterSpacing: '0',
    },

    // Body Bold (14px / 600 / 20px line-height) - labels
    bodyBold: {
      fontSize: '14px',
      fontWeight: '600',
      lineHeight: '20px',
      letterSpacing: '0',
    },

    // Caption (12px / 400 / 16px line-height) - secondary info
    caption: {
      fontSize: '12px',
      fontWeight: '400',
      lineHeight: '16px',
      letterSpacing: '0',
    },

    // Caption Bold (12px / 600 / 16px line-height)
    captionBold: {
      fontSize: '12px',
      fontWeight: '600',
      lineHeight: '16px',
      letterSpacing: '0',
    },

    // Code (13px / 400 / 20px line-height) - monospace data
    code: {
      fontSize: '13px',
      fontWeight: '400',
      lineHeight: '20px',
      letterSpacing: '0',
      fontFamily: '"JetBrains Mono", "Fira Code", "Courier New", monospace',
    },
  },

  // ==========================================
  // SPACING (Pod Ops)
  // ==========================================
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
    xxxl: '64px',
  },

  // ==========================================
  // BORDER RADIUS (Pod Ops)
  // ==========================================
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    full: '9999px',
  },

  // ==========================================
  // SHADOWS (Pod Ops)
  // ==========================================
  shadows: {
    subtle: '0 1px 2px rgba(0, 0, 0, 0.04)',
    small: '0 2px 4px rgba(0, 0, 0, 0.06)',
    medium: '0 4px 8px rgba(0, 0, 0, 0.08)',
    large: '0 8px 16px rgba(0, 0, 0, 0.10)',
    xl: '0 16px 32px rgba(0, 0, 0, 0.12)',
  },

  // ==========================================
  // ANIMATIONS (Pod Ops)
  // ==========================================
  animations: {
    duration: {
      fast: 150,
      normal: 250,
      slow: 350,
    },

    easing: {
      default: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
  },

  // ==========================================
  // Z-INDEX SCALE
  // ==========================================
  zIndex: {
    base: 0,
    dropdown: 1000,
    sticky: 1100,
    overlay: 1200,
    modal: 1300,
    popover: 1400,
    tooltip: 1500,
  },
}

// ==========================================
// UTILITY FUNCTIONS
// ==========================================

/**
 * Get color for device/system status
 * @param status - Status string (case-insensitive)
 * @returns CSS color value
 */
export const getStatusColor = (status: string): string => {
  const statusLower = status.toLowerCase()

  const statusMap: Record<string, string> = {
    // Good/Online States
    'active': theme.colors.deviceStatus.online,
    'online': theme.colors.deviceStatus.online,
    'normal': theme.colors.deviceStatus.online,
    'good': theme.colors.deviceStatus.online,
    'operational': theme.colors.deviceStatus.online,

    // Warning States
    'warning': theme.colors.deviceStatus.warning,
    'caution': theme.colors.deviceStatus.warning,
    'degraded': theme.colors.deviceStatus.warning,
    'attention': theme.colors.deviceStatus.warning,

    // Error/Critical States
    'critical': theme.colors.deviceStatus.error,
    'alarm': theme.colors.deviceStatus.error,
    'error': theme.colors.deviceStatus.error,
    'offline': theme.colors.deviceStatus.offline,
    'failed': theme.colors.deviceStatus.error,

    // Maintenance States
    'maintenance': theme.colors.deviceStatus.maintenance,
    'calibrating': theme.colors.deviceStatus.calibrating,
    'testing': theme.colors.deviceStatus.calibrating,

    // Unknown States
    'unknown': theme.colors.deviceStatus.offline,
    'inactive': theme.colors.deviceStatus.offline,
    'disabled': theme.colors.deviceStatus.offline,
  }

  return statusMap[statusLower] || theme.colors.deviceStatus.offline
}

/**
 * Get color for user role
 * @param role - Role string
 * @returns CSS color value
 */
export const getRoleColor = (role: string): string => {
  const roleLower = role.toLowerCase()

  if (roleLower.includes('admin')) return theme.colors.primary
  if (roleLower.includes('operator')) return theme.colors.text.primary
  if (roleLower.includes('viewer') || roleLower.includes('guest')) return theme.colors.text.tertiary

  return theme.colors.text.secondary
}

/**
 * Get air quality status color
 * @param aqi - Air Quality Index (0-500+)
 * @returns CSS color value
 */
export const getAQIColor = (aqi: number): string => {
  if (aqi <= 50) return theme.colors.aqStatus.good
  if (aqi <= 100) return theme.colors.aqStatus.moderate
  if (aqi <= 150) return theme.colors.aqStatus.unhealthySensitive
  if (aqi <= 200) return theme.colors.aqStatus.unhealthy
  if (aqi <= 300) return theme.colors.aqStatus.veryUnhealthy
  return theme.colors.aqStatus.hazardous
}

// ==========================================
// BACKWARDS COMPATIBILITY
// ==========================================

/**
 * @deprecated Use theme.colors instead
 * Provided for backwards compatibility during migration
 */
export const OCAColors = {
  primary: theme.colors.primary,
  primaryHover: theme.colors.primaryHover,

  statusGood: theme.colors.deviceStatus.online,
  statusWarning: theme.colors.deviceStatus.warning,
  statusCritical: theme.colors.deviceStatus.error,
  statusUnknown: theme.colors.deviceStatus.offline,

  textPrimary: theme.colors.text.primary,
  textSecondary: theme.colors.text.secondary,
  textDisabled: theme.colors.text.disabled,

  border: theme.colors.border,
  borderLight: theme.colors.border,

  background: theme.colors.background,
  backgroundAlt: theme.colors.surface,
  backgroundHover: theme.colors.surfaceHover,

  success: theme.colors.success,
  error: theme.colors.error,
  info: theme.colors.info,

  backgroundSuccess: theme.colors.successLight,
  backgroundWarning: theme.colors.warningLight,
  backgroundError: theme.colors.errorLight,
  backgroundInfo: theme.colors.infoLight,

  chart1: theme.colors.charts.primary,
  chart2: theme.colors.charts.secondary,
  chart3: theme.colors.charts.tertiary,
  chart4: theme.colors.charts.quaternary,
  chart5: theme.colors.charts.quinary,

  // Legacy color constants for backwards compatibility
  TITLE_BAR_GRADIENT: 'linear-gradient(135deg, #009ADA 0%, #004C97 100%)',
  MEDIUM_BLUE: '#009ADA',
  DEEP_BLUE: '#004C97',
  GREEN: '#1CBD52',
  ORANGE: '#FA882A',
}

// Export type for TypeScript
export type Theme = typeof theme

// Default export
export default theme
